import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Box from '@material-ui/core/Box';
import BottomNavigation from '@material-ui/core/BottomNavigation';
import BottomNavigationAction from '@material-ui/core/BottomNavigationAction';
import { Tooltip, Typography, Button, Collapse, Badge } from "@material-ui/core";
import {
  MessageSquare,
  Clock,
  Users,
  Search,
  Archive,
  PlusCircle,
  Eye,
  EyeOff,
  Filter,
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  XCircle,
  FileText,
  File,
  Mail,
  Calendar,
} from "lucide-react";

import TicketsListCustom from "../../components/TicketsListCustom";
import Ticket from "../../components/Ticket/";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import TicketAdvancedLayoutModern from "../../components/TicketAdvancedLayout/TicketAdvancedLayoutModern";
import NewTicketModal from "../../components/NewTicketModal";
import { TagsFilter } from "../../components/TagsFilter";
import { UsersFilter } from "../../components/UsersFilter";
import { StatusFilter } from "../../components/StatusFilter";
import { WhatsappsFilter } from "../../components/WhatsappsFilter";
import TicketsQueueSelect from "../../components/TicketsQueueSelect";
import { Can } from "../../components/Can";
import CloseAllTicketsModal from "../../components/CloseAllTicketsModal";
import TicketMessagesSearchModal from "../../components/TicketMessagesSearchModal";

import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import useTicketCounts from "../../hooks/useTicketCounts";
import api from "../../services/api";

import AccountTreeIcon from "@material-ui/icons/AccountTree";
import PauseCircleFilledIcon from "@material-ui/icons/PauseCircleFilled";
import { i18n } from "../../translate/i18n";
import { QueueSelectedProvider } from "../../context/QueuesSelected/QueuesSelectedContext";
import { ModernLayoutProvider } from "../../context/ModernLayout/ModernLayoutContext";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    overflow: "hidden",
  },
  header: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  bottomNav: {
    backgroundColor: theme.palette.background.paper,
    // Mobile: esconder labels e mostrar apenas ícones com badges
    [theme.breakpoints.down("sm")]: {
      "& .MuiBottomNavigationAction-label": {
        display: "none",
      },
      "& .MuiBottomNavigationAction-root": {
        minWidth: "auto",
        padding: "6px 12px",
      },
    },
  },
  mobileBadge: {
    // Badge visível apenas no mobile (no desktop fica no label)
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      fontSize: "0.65rem",
      minWidth: "18px",
      height: "18px",
      padding: "0 4px",
    },
  },
  mobileBadgeGreen: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      fontSize: "0.65rem",
      minWidth: "18px",
      height: "18px",
      padding: "0 4px",
      backgroundColor: "#4caf50", // Verde
    },
  },
  mobileBadgeRed: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      fontSize: "0.65rem",
      minWidth: "18px",
      height: "18px",
      padding: "0 4px",
      backgroundColor: "#f44336", // Vermelho
    },
  },
  mobileBadgeBlue: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      fontSize: "0.65rem",
      minWidth: "18px",
      height: "18px",
      padding: "0 4px",
      backgroundColor: theme.palette.primary.main, // Azul
    },
  },
  mobileBadgeOrange: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      fontSize: "0.65rem",
      minWidth: "18px",
      height: "18px",
      padding: "0 4px",
      backgroundColor: "#FF9800", // Laranja
    },
  },
  content: {
    flex: 1,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
  },
  ticketView: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  // Estilo para quando está visualizando ticket (sem header)
  contentFullHeight: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  listView: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  placeholderContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    padding: theme.spacing(3),
    textAlign: "center",
    background: theme.palette.background.default,
  },
  placeholderText: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  newTicketButton: {
    borderRadius: "12px",
    padding: theme.spacing(1.2, 3),
    fontWeight: 600,
    textTransform: "none",
  },
  controlsBar: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1),
    gap: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  controlsTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    gap: theme.spacing(1),
  },
  controlButtons: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  controlButton: {
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main,
    },
    "&.active": {
      backgroundColor: theme.palette.primary.main,
      color: "#FFF",
      borderColor: theme.palette.primary.main,
    },
  },
  queueSelect: {
    flex: 1,
    minWidth: 0,
  },
  searchInputWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "8px",
    padding: theme.spacing(0.5, 1),
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
    },
  },
  searchInput: {
    flex: 1,
    border: "none",
    background: "none",
    outline: "none",
    padding: theme.spacing(0.5),
    color: theme.palette.text.primary,
    fontSize: "14px",
    "&::placeholder": {
      color: theme.palette.text.secondary,
    },
  },
  filterContainer: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  filterRow: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  resetFilterButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.error.light,
    color: "#FFF",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "500",
    cursor: "pointer",
    border: "none",
    "&:hover": {
      backgroundColor: theme.palette.error.main,
    },
  },
  ticketsContainer: {
    flex: 1,
    overflow: "hidden",
  },
  tabBadge: {
    backgroundColor: theme.palette.error.main,
    color: "#fff",
    borderRadius: "10px",
    padding: "2px 6px",
    fontSize: "10px",
    fontWeight: "bold",
    marginLeft: theme.spacing(0.5),
  },
}));

const TicketsAdvancedModern = (props) => {
  const classes = useStyles();
  const { ticketId } = useParams();
  const history = useHistory();
  // 0=Atendendo, 1=Aguardando, 2=Grupos (se allowGroup), 3=Flow, 4=Resolvidos
  const [option, setOption] = useState(0);
  const { currentTicket, setCurrentTicket, tabOpen, setTabOpen } = useContext(TicketsContext);
  const { user, socket } = useContext(AuthContext);

  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [searchOnMessages, setSearchOnMessages] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [sortTickets, setSortTickets] = useState(false);
  const [sortOrderMode, setSortOrderMode] = useState("unreadFirst");
  const [forceSearch, setForceSearch] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState([]);
  const [selectedQueueIds, setSelectedQueueIds] = useState(
    user.queues?.map((q) => q.id) || []
  );
  const [closeAllModalOpen, setCloseAllModalOpen] = useState(false);
  const searchInputRef = useRef();
  const [isRefreshing, setIsRefreshing] = useState(0);
  const [openUnreadCount, setOpenUnreadCount] = useState(0);
  const [pendingUnreadCount, setPendingUnreadCount] = useState(0);
  const [groupUnreadCount, setGroupUnreadCount] = useState(0);
  const [openTotalFromList, setOpenTotalFromList] = useState(0);
  const [pendingTotalFromList, setPendingTotalFromList] = useState(0);
  const [groupTotalFromList, setGroupTotalFromList] = useState(0);

  // Sincroniza selectedQueueIds quando user.queues mudar
  useEffect(() => {
    if (user?.queues?.length > 0) {
      const userQueueIds = user.queues.map((q) => q.id);
      setSelectedQueueIds((prev) => {
        // Só atualiza se as filas mudaram
        if (prev.length === 0 || JSON.stringify(prev.sort()) !== JSON.stringify(userQueueIds.sort())) {
          return userQueueIds;
        }
        return prev;
      });
    }
  }, [user?.queues]);

  const { ticketCounts, permissions } = useTicketCounts({
    selectedQueueIds,
    showAll: showAllTickets,
    isRefreshing,
  });

  useEffect(() => {
    if (currentTicket.id !== null) {
      setCurrentTicket({ id: currentTicket.id, code: '#open' });
      setOption(0);
    }
    if (!ticketId) {
      setOption(1);
    }
    return () => {
      setCurrentTicket({ id: null, code: null });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentTicket.id !== null) {
      setOption(0);
    }
  }, [currentTicket]);

  // Sincroniza option da BottomNavigation com tabOpen (para Flow e navegação externa)
  useEffect(() => {
    let idx = 2;
    const pausedIndex = idx++;
    const groupIndex = user.allowGroup ? idx++ : -1;
    const flowIndex = idx++;
    const closedIndex = idx++;
    if (tabOpen === "open") setOption(0);
    else if (tabOpen === "pending") setOption(1);
    else if (tabOpen === "paused") setOption(pausedIndex);
    else if (tabOpen === "group" && groupIndex >= 0) setOption(groupIndex);
    else if (tabOpen === "flow") setOption(flowIndex);
    else if (tabOpen === "closed") setOption(closedIndex);
  }, [tabOpen, user.allowGroup]);

  useEffect(() => {
    setIsRefreshing((prev) => prev + 1);
  }, [user.profile, user.allUserChat, user.allHistoric, user.allowGroup, user.allTicket, showAllTickets]);

  useEffect(() => {
    if (!socket || !user?.companyId) return;

    const companyId = user.companyId;

    const handleTicketEvent = (data) => {
      if (["create", "update", "delete"].includes(data.action)) {
        setIsRefreshing((prev) => prev + 1);
      }
    };

    // Ao receber nova mensagem (appMessage), atualizar listas para o contador de não lidas refletir
    const handleAppMessageEvent = (data) => {
      if (data.action === "create" || data.action === "update") {
        setIsRefreshing((prev) => prev + 1);
      }
    };

    socket.on(`company-${companyId}-ticket`, handleTicketEvent);
    socket.on(`company-${companyId}-appMessage`, handleAppMessageEvent);

    return () => {
      socket.off(`company-${companyId}-ticket`, handleTicketEvent);
      socket.off(`company-${companyId}-appMessage`, handleAppMessageEvent);
    };
  }, [socket, user]);

  const handleSearchInputChange = (e) => {
    const value = e?.target?.value || '';
    setSearchInputValue(value.toLowerCase());
  };

  const executeSearch = () => {
    const searchedTerm = searchInputValue.trim();
    setSearchParam(searchedTerm);

    if (searchedTerm === "") {
      setTabOpen("open");
      return;
    }

    setTabOpen("search");
    setForceSearch(!forceSearch);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      executeSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchInputValue("");
    setSearchParam("");
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    if (selectedTags.length === 0 && selectedUsers.length === 0 && 
        selectedWhatsapp.length === 0 && selectedStatus.length === 0) {
      setTabOpen("open");
    }
  };

  const handleFilterToggle = () => {
    const newFilterState = !isFilterActive;
    setIsFilterActive(newFilterState);

    if (newFilterState && tabOpen !== "search") {
      setTabOpen("search");
    } else if (!newFilterState && searchParam === "" && 
               selectedTags.length === 0 && selectedUsers.length === 0 && 
               selectedWhatsapp.length === 0 && selectedStatus.length === 0) {
      setTabOpen("open");
    }
  };

  const handleSelectedTags = (selecteds) => {
    const tags = selecteds.map((t) => t.id);
    setSelectedTags(tags);
    if (tags.length > 0 && tabOpen !== "search") setTabOpen("search");
    setForceSearch(!forceSearch);
  };

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);
    setSelectedUsers(users);
    if (users.length > 0) {
      setShowAllTickets(!(users.includes(user.id) && users.length === 1));
      if (tabOpen !== "search") setTabOpen("search");
    } else {
      setShowAllTickets(false);
    }
    setForceSearch(!forceSearch);
  };

  const handleSelectedWhatsapps = (selecteds) => {
    const whatsapp = selecteds.map((t) => t.id);
    setSelectedWhatsapp(whatsapp);
    if (whatsapp.length > 0 && tabOpen !== "search") setTabOpen("search");
    setForceSearch(!forceSearch);
  };

  const handleSelectedStatus = (selecteds) => {
    const statusFilter = selecteds.map((t) => t.status);
    setSelectedStatus(statusFilter);
    if (statusFilter.length > 0 && tabOpen !== "search") setTabOpen("search");
    setForceSearch(!forceSearch);
  };

  const resetAllFilters = () => {
    setSelectedTags([]);
    setSelectedUsers([]);
    setSelectedWhatsapp([]);
    setSelectedStatus([]);
    setSearchParam("");
    setSearchInputValue("");
    if (searchInputRef.current) searchInputRef.current.value = "";
    setTabOpen("open");
    setForceSearch(!forceSearch);
    setIsFilterActive(false);
  };

  const handleCloseAllTickets = () => {
    setCloseAllModalOpen(true);
  };

  const CloseAllTicket = async () => {
    try {
      const hasFilters =
        selectedTags.length > 0 ||
        selectedUsers.length > 0 ||
        selectedWhatsapp.length > 0 ||
        selectedStatus.length > 0;
      const hasSearch = !!(searchParam && searchParam.trim());

      const { data } = await api.post("/tickets/closeAll", {
        status: hasFilters || hasSearch ? "search" : tabOpen,
        selectedQueueIds,
        tags: selectedTags,
        users: selectedUsers,
        whatsappIds: selectedWhatsapp,
        statusFilter: selectedStatus,
        showAll: showAllTickets,
        searchParam: searchParam || "",
        searchOnMessages
      });
      setCloseAllModalOpen(false);
      setIsRefreshing((prev) => prev + 1);
      if (data?.closedCount != null) {
        toast.success(
          data.closedCount === 0
            ? "Nenhum ticket correspondente aos filtros para fechar."
            : `${data.closedCount} ticket(s) fechado(s) conforme os filtros.`
        );
      }
    } catch (err) {
      console.log("Error closing tickets:", err);
      toastError?.(err);
    }
  };

  const handleToggleShowAll = () => {
    if (permissions.isAdmin || permissions.canSeeAllTickets || 
        permissions.canSeeAllUserChats || permissions.canSeeAllHistory) {
      setShowAllTickets(!showAllTickets);
    }
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      setTabOpen("open");
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const getTabLabel = (label, count) => {
    return (
      <span style={{ display: 'flex', alignItems: 'center' }}>
        {label}
        {count > 0 && <span className={classes.tabBadge}>{count}</span>}
      </span>
    );
  };

  const renderPlaceholder = () => {
    return (
      <Box className={classes.placeholderContainer}>
        <Typography variant="body1" className={classes.placeholderText}>
          {i18n.t("chat.noTicketMessage")}
        </Typography>
        <Button 
          onClick={() => setNewTicketModalOpen(true)} 
          variant="contained" 
          color="primary"
          className={classes.newTicketButton}
          startIcon={<PlusCircle size={18} />}
        >
          {i18n.t("ticketsList.actions.newAttendance")}
        </Button>
      </Box>
    );
  };

  const renderMessageContext = () => {
    if (ticketId && ticketId !== "undefined") {
      return <div className={classes.ticketView}><Ticket /></div>;
    }
    return renderPlaceholder();
  };

  const renderTicketsList = () => {
    return (
      <div className={classes.listView}>
        <div className={classes.controlsBar}>
          <div className={classes.controlsTopRow}>
            <div className={classes.controlButtons}>
              <Can
                role={(user.allUserChat === "enabled" || user.allHistoric === "enabled") && 
                      user.profile === "user" ? "admin" : user.profile}
                perform="tickets-manager:showall"
                yes={() => (
                  <Tooltip title={showAllTickets ? i18n.t("ticketsList.actions.showMyTickets") : i18n.t("ticketsList.actions.showAll")} arrow>
                    <div
                      className={`${classes.controlButton} ${showAllTickets ? "active" : ""}`}
                      onClick={handleToggleShowAll}
                    >
                      {showAllTickets ? <Eye size={16} /> : <EyeOff size={16} />}
                    </div>
                  </Tooltip>
                )}
              />

              <Tooltip title={isFilterActive ? i18n.t("ticketsList.actions.closeFilters") : i18n.t("ticketsList.actions.openFilters")} arrow>
                <div
                  className={`${classes.controlButton} ${isFilterActive ? "active" : ""}`}
                  onClick={handleFilterToggle}
                >
                  <Filter size={16} />
                </div>
              </Tooltip>

              <Tooltip title={sortTickets ? i18n.t("ticketsList.actions.newestFirst") : i18n.t("ticketsList.actions.oldestFirst")} arrow>
                <div
                  className={`${classes.controlButton} ${sortTickets ? "active" : ""}`}
                  onClick={() => setSortTickets(!sortTickets)}
                >
                  {sortTickets ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                </div>
              </Tooltip>

              {user.profile === "admin" && tabOpen !== "closed" && (
                <Tooltip title={i18n.t("ticketsList.actions.closeAllTickets")} arrow>
                  <div className={classes.controlButton} onClick={handleCloseAllTickets}>
                    <CheckCircle2 size={16} />
                  </div>
                </Tooltip>
              )}

              <Tooltip title={i18n.t("ticketsList.sortUnreadFirst")} arrow>
                <div
                  className={`${classes.controlButton} ${sortOrderMode === "unreadFirst" ? "active" : ""}`}
                  onClick={() => setSortOrderMode("unreadFirst")}
                >
                  <Mail size={16} />
                </div>
              </Tooltip>
              <Tooltip title={i18n.t("ticketsList.sortByDate")} arrow>
                <div
                  className={`${classes.controlButton} ${sortOrderMode === "byDate" ? "active" : ""}`}
                  onClick={() => setSortOrderMode("byDate")}
                >
                  <Calendar size={16} />
                </div>
              </Tooltip>

              <Tooltip title={i18n.t("ticketsList.actions.newTicket")} arrow>
                <div className={classes.controlButton} onClick={() => setNewTicketModalOpen(true)}>
                  <PlusCircle size={16} />
                </div>
              </Tooltip>
            </div>

            <div className={classes.queueSelect}>
              <TicketsQueueSelect
                selectedQueueIds={selectedQueueIds}
                userQueues={user?.queues}
                onChange={setSelectedQueueIds}
              />
            </div>
          </div>

          <Collapse in={tabOpen === "search" || isFilterActive}>
            <div className={classes.searchInputWrapper}>
              <Search size={16} />
              <input
                className={classes.searchInput}
                placeholder={i18n.t("tickets.search.placeholder")}
                value={searchInputValue}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
                ref={searchInputRef}
              />
              <div className={classes.controlButton} onClick={executeSearch} style={{ width: 24, height: 24 }}>
                <Search size={12} />
              </div>
              {(searchInputValue || searchParam) && (
                <div className={classes.controlButton} onClick={handleClearSearch} style={{ width: 24, height: 24 }}>
                  <XCircle size={12} />
                </div>
              )}
              <div
                className={`${classes.controlButton} ${searchOnMessages ? "active" : ""}`}
                onClick={() => {
                  setSearchOnMessages(!searchOnMessages);
                  if (searchParam && tabOpen === "search") setForceSearch(!forceSearch);
                }}
                style={{ width: 24, height: 24 }}
              >
                {searchOnMessages ? <FileText size={12} /> : <File size={12} />}
              </div>
            </div>
          </Collapse>
        </div>

        {isFilterActive && (
          <div className={classes.filterContainer}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="caption" color="textSecondary">
                {i18n.t("ticketsList.actions.appliedFilters")}
              </Typography>
              <button className={classes.resetFilterButton} onClick={resetAllFilters}>
                <XCircle size={12} />
                {i18n.t("ticketsList.actions.clear")}
              </button>
            </div>
            <div className={classes.filterRow}>
              <TagsFilter onFiltered={handleSelectedTags} />
              <WhatsappsFilter onFiltered={handleSelectedWhatsapps} />
              <StatusFilter onFiltered={handleSelectedStatus} />
              {user.profile === "admin" && <UsersFilter onFiltered={handleSelectedUsers} />}
            </div>
          </div>
        )}

        <div className={classes.ticketsContainer}>
          {/* Listas sempre montadas (ocultas quando inativas) para atualizar contadores de não lidas */}
          <TicketsListCustom
            key={`open-${selectedQueueIds.join("-")}-${showAllTickets}-${sortTickets}-${sortOrderMode}`}
            status="open"
            showAll={showAllTickets}
            sortTickets={sortTickets ? "ASC" : "DESC"}
            sortOrderMode={sortOrderMode}
            selectedQueueIds={selectedQueueIds}
            searchParam={searchParam}
            searchOnMessages={searchOnMessages}
            setTabOpen={setTabOpen}
            updateUnreadCount={setOpenUnreadCount}
            updateTotalCount={setOpenTotalFromList}
            refreshTrigger={isRefreshing}
            onTicketStatusChange={() => setIsRefreshing((prev) => prev + 1)}
            style={tabOpen !== "open" ? { width: 0, height: 0, overflow: "hidden", position: "absolute" } : undefined}
          />

          <TicketsListCustom
            key={`pending-${selectedQueueIds.join("-")}-${showAllTickets}-${sortTickets}-${sortOrderMode}`}
            status="pending"
            selectedQueueIds={selectedQueueIds}
            sortTickets={sortTickets ? "ASC" : "DESC"}
            sortOrderMode={sortOrderMode}
            searchParam={searchParam}
            searchOnMessages={searchOnMessages}
            showAll={user.profile === "admin" || user.allUserChat === "enabled" ||
                     user.allHistoric === "enabled" ? showAllTickets : false}
            setTabOpen={setTabOpen}
            updateUnreadCount={setPendingUnreadCount}
            updateTotalCount={setPendingTotalFromList}
            refreshTrigger={isRefreshing}
            onTicketStatusChange={() => setIsRefreshing((prev) => prev + 1)}
            style={tabOpen !== "pending" ? { width: 0, height: 0, overflow: "hidden", position: "absolute" } : undefined}
          />

          {user.allowGroup && (
            <TicketsListCustom
              key={`group-${selectedQueueIds.join("-")}-${showAllTickets}-${sortTickets}-${sortOrderMode}`}
              status="group"
              showAll={showAllTickets}
              sortTickets={sortTickets ? "ASC" : "DESC"}
              sortOrderMode={sortOrderMode}
              selectedQueueIds={selectedQueueIds}
              searchParam={searchParam}
              searchOnMessages={searchOnMessages}
              setTabOpen={setTabOpen}
              updateUnreadCount={setGroupUnreadCount}
              updateTotalCount={setGroupTotalFromList}
              refreshTrigger={isRefreshing}
              onTicketStatusChange={() => setIsRefreshing((prev) => prev + 1)}
              style={tabOpen !== "group" ? { width: 0, height: 0, overflow: "hidden", position: "absolute" } : undefined}
            />
          )}

          {tabOpen === "paused" && (
            <TicketsListCustom
              key={`paused-${selectedQueueIds.join("-")}-${showAllTickets}-${sortTickets}-${sortOrderMode}`}
              status="paused"
              showAll={showAllTickets}
              sortTickets={sortTickets ? "ASC" : "DESC"}
              sortOrderMode={sortOrderMode}
              selectedQueueIds={selectedQueueIds}
              searchParam={searchParam}
              searchOnMessages={searchOnMessages}
              setTabOpen={setTabOpen}
              refreshTrigger={isRefreshing}
              onTicketStatusChange={() => setIsRefreshing((prev) => prev + 1)}
            />
          )}

          {tabOpen === "flow" && (
            <TicketsListCustom
              key={`flow-${selectedQueueIds.join("-")}-${showAllTickets}-${sortTickets}-${sortOrderMode}`}
              status="flow"
              showAll={showAllTickets}
              sortTickets={sortTickets ? "ASC" : "DESC"}
              sortOrderMode={sortOrderMode}
              selectedQueueIds={selectedQueueIds}
              searchParam={searchParam}
              searchOnMessages={searchOnMessages}
              setTabOpen={setTabOpen}
              onTicketStatusChange={() => setIsRefreshing((prev) => prev + 1)}
            />
          )}

          {tabOpen === "closed" && (
            <TicketsListCustom
              key={`closed-${selectedQueueIds.join("-")}-${showAllTickets}-${sortTickets}-${sortOrderMode}`}
              status="closed"
              showAll={showAllTickets}
              sortTickets={sortTickets ? "ASC" : "DESC"}
              sortOrderMode={sortOrderMode}
              selectedQueueIds={selectedQueueIds}
              searchParam={searchParam}
              searchOnMessages={searchOnMessages}
              setTabOpen={setTabOpen}
            />
          )}

          {tabOpen === "search" && (
            <TicketsListCustom
              key={`search-${forceSearch}-${sortOrderMode}`}
              statusFilter={selectedStatus}
              searchParam={searchParam}
              showAll={showAllTickets}
              sortTickets={sortTickets ? "ASC" : "DESC"}
              sortOrderMode={sortOrderMode}
              tags={selectedTags}
              users={selectedUsers}
              selectedQueueIds={selectedQueueIds}
              whatsappIds={selectedWhatsapp}
              forceSearch={forceSearch}
              searchOnMessages={searchOnMessages}
              status="search"
              setTabOpen={setTabOpen}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <QueueSelectedProvider>
      <ModernLayoutProvider>
        <NewTicketModal
          modalOpen={newTicketModalOpen}
          onClose={handleCloseOrOpenTicket}
        />
        <CloseAllTicketsModal
          open={closeAllModalOpen}
          onClose={() => setCloseAllModalOpen(false)}
          onConfirm={CloseAllTicket}
        />
        
        <TicketAdvancedLayoutModern>
        <Box className={classes.root}>
          {/* Esconde header quando está visualizando um ticket no mobile */}
          {!(ticketId && ticketId !== "undefined") && (
            <Box className={classes.header}>
              <BottomNavigation
                value={option}
                onChange={(event, newValue) => setOption(newValue)}
                showLabels
                className={classes.bottomNav}
              >
                <BottomNavigationAction 
                  label={getTabLabel(i18n.t("ticketsList.assignedHeader"), openUnreadCount > 0 ? openUnreadCount : (openTotalFromList > 0 ? openTotalFromList : ticketCounts.open))} 
                  icon={
                    <Badge 
                      badgeContent={openUnreadCount > 0 ? openUnreadCount : (openTotalFromList > 0 ? openTotalFromList : ticketCounts.open)} 
                      max={999}
                      classes={{ badge: openUnreadCount > 0 ? classes.mobileBadgeRed : classes.mobileBadgeGreen }}
                    >
                      <MessageSquare size={20} />
                    </Badge>
                  }
                  onClick={() => setTabOpen("open")}
                />
                <BottomNavigationAction 
                  label={getTabLabel(i18n.t("ticketsList.pendingHeader"), pendingUnreadCount > 0 ? pendingUnreadCount : Math.max(pendingTotalFromList || 0, ticketCounts.pending || 0))} 
                  icon={
                    <Badge 
                      badgeContent={pendingUnreadCount > 0 ? pendingUnreadCount : Math.max(pendingTotalFromList || 0, ticketCounts.pending || 0)} 
                      max={999}
                      classes={{ badge: pendingUnreadCount > 0 ? classes.mobileBadgeRed : classes.mobileBadgeGreen }}
                    >
                      <Clock size={20} />
                    </Badge>
                  }
                  onClick={() => setTabOpen("pending")}
                />
                <BottomNavigationAction
                  label={getTabLabel("Em Pausa", ticketCounts.paused || 0)}
                  icon={
                    <Badge
                      badgeContent={ticketCounts.paused || 0}
                      max={999}
                      classes={{ badge: classes.mobileBadgeOrange }}
                      overlap="circular"
                      invisible={!ticketCounts.paused}
                    >
                      <PauseCircleFilledIcon style={{ fontSize: 20, color: tabOpen === "paused" ? "#FF9800" : undefined }} />
                    </Badge>
                  }
                  onClick={() => setTabOpen("paused")}
                />
                {user.allowGroup && (
                  <BottomNavigationAction
                    label={getTabLabel(i18n.t("ticketsList.groupingHeader"), groupUnreadCount > 0 ? groupUnreadCount : (groupTotalFromList > 0 ? groupTotalFromList : ticketCounts.group))}
                    icon={
                      <Badge
                        badgeContent={groupUnreadCount > 0 ? groupUnreadCount : (groupTotalFromList > 0 ? groupTotalFromList : ticketCounts.group)}
                        max={999}
                        classes={{ badge: groupUnreadCount > 0 ? classes.mobileBadgeRed : classes.mobileBadgeBlue }}
                      >
                        <Users size={20} />
                      </Badge>
                    }
                    onClick={() => setTabOpen("group")}
                  />
                )}
                <BottomNavigationAction
                  label={getTabLabel(i18n.t("ticketsList.flowHeader") || "Flow", ticketCounts.flow)}
                  icon={
                    <Badge
                      badgeContent={ticketCounts.flow}
                      max={999}
                      color="primary"
                      overlap="circular"
                      invisible={ticketCounts.flow === 0}
                    >
                      <AccountTreeIcon style={{ fontSize: 20 }} />
                    </Badge>
                  }
                  onClick={() => setTabOpen("flow")}
                />
                <BottomNavigationAction 
                  label={i18n.t("ticketsList.closedHeader")} 
                  icon={<Archive size={20} />}
                  onClick={() => setTabOpen("closed")}
                />
              </BottomNavigation>
            </Box>
          )}
          <Box className={ticketId && ticketId !== "undefined" ? classes.contentFullHeight : classes.content}>
            {ticketId && ticketId !== "undefined" ? renderMessageContext() : renderTicketsList()}
          </Box>
        </Box>
        <TicketMessagesSearchModal
          ticketUuid={ticketId && ticketId !== "undefined" ? ticketId : null}
          searchOnMessages={searchOnMessages}
          rawSearchInput={searchInputValue}
          ticketSelectionKey={currentTicket?.code}
        />
        </TicketAdvancedLayoutModern>
      </ModernLayoutProvider>
    </QueueSelectedProvider>
  );
};

export default TicketsAdvancedModern;

